import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Gift, 
  Copy, 
  Check, 
  Users, 
  Clock, 
  Award,
  Share2,
  Mail,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { useReferral } from "@/hooks/useReferral";
import { Skeleton } from "@/components/ui/skeleton";

export const ReferralSection = () => {
  const { referralCode, stats, loading, getShareUrl, getShareText } = useReferral();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement("textarea");
      textarea.value = referralCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyLink = async () => {
    const url = getShareUrl();
    if (!url) return;
    
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("Lien copié !");
    }
  };

  const handleShareEmail = () => {
    const url = getShareUrl();
    const text = getShareText();
    const subject = "Découvrez EasyFinder - Gestion intelligente de documents";
    const body = `${text}\n\n${url}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleShareWhatsApp = () => {
    const url = getShareUrl();
    const text = getShareText();
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`);
  };

  const handleNativeShare = async () => {
    const url = getShareUrl();
    const text = getShareText();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "EasyFinder - Parrainage",
          text: text,
          url: url || undefined,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          Programme de parrainage
        </CardTitle>
        <CardDescription>
          Invitez vos contacts et gagnez des récompenses
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Referral Code */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Votre code de parrainage</label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                value={referralCode || ""}
                readOnly
                className="font-mono text-lg tracking-wider text-center bg-muted/50"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyCode}
              className="shrink-0"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Share Options */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Partager</label>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyLink}>
              <Copy className="w-4 h-4 mr-2" />
              Copier le lien
            </Button>
            <Button variant="outline" size="sm" onClick={handleShareEmail}>
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button variant="outline" size="sm" onClick={handleShareWhatsApp} className="bg-green-50 hover:bg-green-100 border-green-200">
              <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
              WhatsApp
            </Button>
            {navigator.share && (
              <Button variant="outline" size="sm" onClick={handleNativeShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Partager
              </Button>
            )}
          </div>
        </div>

        <Separator />

        {/* Stats */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Vos statistiques</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-2xl font-bold">{stats?.total_referrals || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Parrainages total</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-2xl font-bold">{stats?.pending_referrals || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">En attente</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-2xl font-bold">{stats?.completed_referrals || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Complétés</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Award className="w-4 h-4 text-primary" />
                <span className="text-2xl font-bold">{stats?.rewards_earned || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Récompenses</p>
            </div>
          </div>
        </div>

        {/* Rewards Info */}
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Gift className="w-4 h-4 text-primary" />
            Comment ça marche ?
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Partagez votre code avec vos contacts</li>
            <li>• Ils s'inscrivent avec votre code</li>
            <li>• Quand ils souscrivent un abonnement, vous gagnez tous les deux !</li>
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">
              +1 mois gratuit pour vous
            </Badge>
            <Badge variant="secondary" className="text-xs">
              -20% pour le filleul
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
